import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Action } from '@/authorization/action';
import { Subject } from '@/authorization/subject';
import { useAppAbility } from '@/authorization/use-app-ability';
import { RetryQueryAlert } from '@/components/alerts/retry-query-alert';
import { CountryRiskBadge } from '@/components/country-risk-badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useLocalizedCountryName } from '@/hooks/use-localized-country';
import { GET_COUNTRY_RISKS_KEY } from '@/keys';
import { useUpdateCountryRisk } from '@/services/apis/mutations/country/use-update-country-risk';
import { useGetCountryRisks } from '@/services/apis/queries/country/use-get-country-risks';
import { COUNTRY_RISK_LEVELS } from '@/types/core/country-risk';

export function CountryRiskSettings() {
  const { t } = useTranslation('configuration');
  const { t: common } = useTranslation('common');
  const ability = useAppAbility();
  const canManage = ability.can(Action.Manage, Subject.ProductionDeclaration);
  const { data: countries = [], isPending, isError } = useGetCountryRisks();
  const update = useUpdateCountryRisk();
  const { getLocalizedCountry } = useLocalizedCountryName();
  const [search, setSearch] = useState('');
  const rows = countries
    .map((country) => ({
      ...country,
      label:
        country.name ??
        (country.isoCode ? getLocalizedCountry(country.isoCode) : null) ??
        String(country.id),
    }))
    .filter((country) =>
      `${country.label} ${country.name ?? ''} ${country.isoCode ?? ''}`
        .toLocaleLowerCase()
        .includes(search.toLocaleLowerCase()),
    );

  return (
    <section
      className='space-y-4 p-4 sm:p-6'
      aria-labelledby='country-risk-title'
    >
      <div>
        <h2 className='font-semibold text-lg' id='country-risk-title'>
          {t('countryRisk.title')}
        </h2>
        <p className='text-muted-foreground text-sm'>
          {t('countryRisk.description')}
        </p>
      </div>
      <div className='max-w-sm space-y-2'>
        <Label htmlFor='country-risk-search'>{t('countryRisk.search')}</Label>
        <Input
          id='country-risk-search'
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>
      {isError ? (
        <RetryQueryAlert
          message={t('countryRisk.loadError')}
          queryKey={GET_COUNTRY_RISKS_KEY}
        />
      ) : null}
      {isPending ? <p role='status'>{t('countryRisk.loading')}</p> : null}
      {isPending || isError ? null : (
        <div className='max-h-[32rem] overflow-auto rounded-md border'>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('countryRisk.country')}</TableHead>
                <TableHead>{t('countryRisk.iso')}</TableHead>
                <TableHead>{t('countryRisk.level')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((country) => (
                <TableRow key={country.id}>
                  <TableCell>{country.label}</TableCell>
                  <TableCell>{country.isoCode?.toUpperCase() ?? '—'}</TableCell>
                  <TableCell>
                    {canManage ? (
                      <Select
                        value={country.risk ?? ''}
                        disabled={update.isPending}
                        onValueChange={(value) => {
                          const risk = COUNTRY_RISK_LEVELS.find(
                            (level) => level === value,
                          );
                          if (!risk || risk === country.risk) {
                            return;
                          }
                          update.mutate(
                            { id: country.id, risk },
                            {
                              onSuccess: () =>
                                toast.success(t('countryRisk.saved')),
                              onError: () =>
                                toast.error(t('countryRisk.saveError')),
                            },
                          );
                        }}
                      >
                        <SelectTrigger
                          aria-label={t('countryRisk.editLabel', {
                            country: country.label,
                          })}
                          className='w-44'
                        >
                          <SelectValue
                            placeholder={common('countryRisk.unavailable')}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {COUNTRY_RISK_LEVELS.map((risk) => (
                            <SelectItem key={risk} value={risk}>
                              {common(`countryRisk.${risk}`)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <CountryRiskBadge risk={country.risk} />
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3}>{t('countryRisk.empty')}</TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </div>
      )}
    </section>
  );
}
